import { Check, ChevronDown, MapPin, MapPinned, PencilLine } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { MAX_ITEM_TEXT_LENGTH } from '../lib/item-values';
import type { PlaceKind } from '../services/items';
import type { ItemRecord } from '../types';

type Props = {
  items: ItemRecord[];
  onRename: (kind: PlaceKind, previousName: string, nextName: string) => Promise<number>;
};

type PlaceSummary = { name: string; count: number };

export function PlaceManager({ items, onRename }: Props) {
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busyKey, setBusyKey] = useState('');
  const [message, setMessage] = useState('');
  const locations = useMemo(() => summarize(items, 'locationNote'), [items]);
  const groups = useMemo(() => summarize(items, 'locationGroup'), [items]);

  const rename = async (kind: PlaceKind, place: PlaceSummary) => {
    const key = placeKey(kind, place.name);
    const next = (drafts[key] ?? place.name).trim();
    setBusyKey(key);
    setMessage('');
    try {
      const count = await onRename(kind, place.name, next);
      setMessage(count
        ? `Updated ${count} item${count === 1 ? '' : 's'} from “${place.name}” to “${next}”.`
        : 'That name is already up to date.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'This place could not be renamed.');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="place-manager">
      <div className="place-manager-heading">
        <div>
          <h3><MapPinned size={19} />Places and gallery sections</h3>
          <p>Correct a name once and every matching item updates together.</p>
        </div>
        <button className="button secondary compact" type="button" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
          <PencilLine size={16} />Manage names<ChevronDown className={open ? 'chevron-open' : ''} size={16} />
        </button>
      </div>

      {open ? (
        <div className="place-manager-content">
          <PlaceList
            title="Exact places"
            description="The precise room, shelf, drawer, or spot shown on each item."
            icon={<MapPin size={16} aria-hidden="true" />}
            kind="location"
            places={locations}
            drafts={drafts}
            busyKey={busyKey}
            onDraft={(key, value) => setDrafts((current) => ({ ...current, [key]: value }))}
            onRename={rename}
          />
          <PlaceList
            title="Gallery sections"
            description="The headings used when the gallery is grouped by place."
            icon={<MapPinned size={16} aria-hidden="true" />}
            kind="group"
            places={groups}
            drafts={drafts}
            busyKey={busyKey}
            onDraft={(key, value) => setDrafts((current) => ({ ...current, [key]: value }))}
            onRename={rename}
          />
          {message ? <p className="place-manager-message" aria-live="polite">{message}</p> : null}
        </div>
      ) : null}
    </div>
  );
}

function PlaceList({ title, description, icon, kind, places, drafts, busyKey, onDraft, onRename }: {
  title: string;
  description: string;
  icon: ReactNode;
  kind: PlaceKind;
  places: PlaceSummary[];
  drafts: Record<string, string>;
  busyKey: string;
  onDraft: (key: string, value: string) => void;
  onRename: (kind: PlaceKind, place: PlaceSummary) => Promise<void>;
}) {
  return (
    <section className="place-list" aria-labelledby={`place-list-${kind}`}>
      <div className="place-list-title">
        <h4 id={`place-list-${kind}`}>{icon}{title}</h4>
        <p>{description}</p>
      </div>
      {places.length ? places.map((place) => {
        const key = placeKey(kind, place.name);
        const value = drafts[key] ?? place.name;
        const unchanged = value.trim() === place.name;
        return (
          <div className="place-row" key={key}>
            <label>
              <span className="visually-hidden">Rename {place.name}</span>
              <input value={value} maxLength={MAX_ITEM_TEXT_LENGTH} onChange={(event) => onDraft(key, event.target.value)} />
            </label>
            <span className="place-count">{place.count} item{place.count === 1 ? '' : 's'}</span>
            <button className="button secondary compact" type="button" disabled={busyKey === key || !value.trim() || unchanged} onClick={() => void onRename(kind, place)}>
              <Check size={15} />Save
            </button>
          </div>
        );
      }) : <p className="place-list-empty">No saved names yet.</p>}
    </section>
  );
}

function summarize(items: ItemRecord[], field: 'locationNote' | 'locationGroup') {
  const counts = new Map<string, number>();
  for (const item of items) {
    const name = item[field].trim();
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts].map(([name, count]) => ({ name, count })).toSorted((a, b) => a.name.localeCompare(b.name));
}

function placeKey(kind: PlaceKind, name: string) {
  return `${kind}:${name}`;
}
